import { Entity, Property as IgnitionProperty } from '@square/ignition';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { AnchoredHeading } from '@squareup/dex-ui';
import { Box, BoxProps } from '@squareup/dex-ui-shared-base';
import clsx from 'clsx';
import React, { FunctionComponent, ReactNode, memo } from 'react';

import { PropertyStatus } from '../../PropertyStatus';
import { PropertyType } from '../../PropertyType';
import { PropertyCategory } from '../../types';

import { PropertyName } from './PropertyName';
import { usePropertyAnchor } from './hooks';
import styles from './property-info.module.css';

const strings = {
  required: 'Required',
  beta: 'Beta',
  deprecated: 'Deprecated',
  alpha: 'Alpha',
};

interface PropertyProps {
  category: PropertyCategory | null;
  disableAnchor?: boolean;
  property: IgnitionProperty;
  narrowView?: boolean;
  entityStack?: Entity[];
  preChildren?: ReactNode;
  postChildren?: ReactNode;
  showTooltip?: boolean;
}

const PropertyInfo: FunctionComponent<
  PropertyProps & NullableClassName & Pick<BoxProps, 'margin'>
> = memo(
  ({
    category,
    disableAnchor = false,
    property,
    narrowView = false,
    entityStack = [],
    preChildren,
    postChildren,
    className,
    showTooltip = false,
    margin,
  }) => {
    const anchor = usePropertyAnchor(category, property.name);

    const propertyName = (
      <PropertyName
        description={property.description}
        name={property.name}
        showTooltip={showTooltip}
      />
    );

    const heading =
      anchor && !disableAnchor ? (
        <AnchoredHeading
          trackingId={`property-info-content-${property.name}`}
          anchor={anchor}
          alignment={narrowView ? 'baseline' : 'center'}
          aria-label={`Copy link to ${property.name}`}
          aria-describedby={
            'Copies a link directly to this property into your clipboard '
          }
        >
          {propertyName}
        </AnchoredHeading>
      ) : (
        <Box>{propertyName}</Box>
      );

    const getMargin = (): BoxProps['margin'] => {
      if (margin) {
        return margin;
      }

      return narrowView ? { bottom: '0.5x' } : undefined;
    };

    return (
      <Box
        className={clsx(
          styles['property-info'],
          narrowView && styles['nested-view'],
          className
        )}
        margin={getMargin()}
      >
        {preChildren}
        <Box>{heading}</Box>
        <Box margin={narrowView ? undefined : { bottom: '1x' }}>
          <PropertyType
            property={property}
            entityStack={entityStack}
          ></PropertyType>
        </Box>
        {property.required && (
          <PropertyStatus variant="required">{strings.required}</PropertyStatus>
        )}
        {property.isBeta && (
          <PropertyStatus variant="beta">{strings.beta}</PropertyStatus>
        )}
        {property.isDeprecated && (
          <PropertyStatus variant="deprecated">
            {strings.deprecated}
          </PropertyStatus>
        )}
        {property.isAlpha && (
          <PropertyStatus variant="alpha">{strings.alpha}</PropertyStatus>
        )}
        {postChildren}
      </Box>
    );
  }
);

PropertyInfo.displayName = 'PropertyInfo';

export { PropertyInfo };

export { PropertyCategory } from '../../types';
